/**
 * Virtual Cart Manager
 * Manages cart items in LocalStorage before creating Shopify variants
 */
class VirtualCart {
  constructor() {
    this.storageKey = 'stickerplease_virtual_cart';
    this.ajaxUrl = 'https://api.stickerplease.com/index.php';
    this.wholesaleDiscount = 0.10; // 10% discount
  }

  /**
   * Check if customer is wholesale
   */
  isWholesale() {
    // Try to get from cart drawer element
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const isWholesaleAttr = cartDrawer.getAttribute('data-is-wholesale');
      if (isWholesaleAttr !== null) {
        return isWholesaleAttr === 'true';
      }
    }
    
    // Fallback: try to get from global variable if set by Liquid
    if (typeof window.isWholesaleCustomer !== 'undefined') {
      return window.isWholesaleCustomer === true;
    }
    
    return false;
  }

  /**
   * Apply wholesale discount to price
   */
  applyWholesaleDiscount(price) {
    if (this.isWholesale() && price > 0) {
      return price * (1 - this.wholesaleDiscount);
    }
    return price;
  }

  /**
   * Generate unique ID for cart items
   */
  generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Get all items from LocalStorage
   */
  getItems() {
    try {
      const items = localStorage.getItem(this.storageKey);
      return items ? JSON.parse(items) : [];
    } catch (error) {
      console.error('Error reading from LocalStorage:', error);
      return [];
    }
  }

  /**
   * Save items to LocalStorage
   */
  saveItems(items) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
      return true;
    } catch (error) {
      console.error('Error saving to LocalStorage:', error);
      // If storage is full, try to clear old items
      if (error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Clearing old items.');
        this.clear();
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(items));
          return true;
        } catch (e) {
          console.error('Still unable to save after clearing:', e);
          return false;
        }
      }
      return false;
    }
  }

  /**
   * Add item to virtual cart
   */
  addItem(itemData) {
    const items = this.getItems();
    
    // Get original price (use provided originalPrice or fallback to price)
    const originalPrice = parseFloat(itemData.originalPrice || itemData.price || 0);
    
    // Apply wholesale discount if customer is wholesale
    const finalPrice = this.applyWholesaleDiscount(originalPrice);
    const isWholesale = this.isWholesale();
    
    const newItem = {
      id: this.generateUniqueId(),
      needsCreation: itemData.needsCreation !== undefined ? itemData.needsCreation : true,
      variantId: itemData.variantId || null,
      quantity: itemData.quantity || 1,
      price: finalPrice, // Final price after discount
      originalPrice: originalPrice, // Original price before discount
      isWholesale: isWholesale, // Flag to indicate if wholesale discount was applied
      formData: itemData.formData || null,
      productId: itemData.productId || null,
      // For regular products (not custom stickers)
      regularVariantId: itemData.regularVariantId || null,
      // Store image URL for display
      imageUrl: itemData.imageUrl || null,
      // Store product title for display
      title: itemData.title || 'Custom Sticker',
      // Store product handle for CSS class
      productHandle: itemData.productHandle || null,
      // Store variant options for display (array of {name: string, value: string})
      variantOptions: itemData.variantOptions || null
    };

    // If formData exists and needs creation, update price in formData for API
    if (newItem.formData && newItem.needsCreation) {
      newItem.formData.price = finalPrice;
    }

    items.push(newItem);
    this.saveItems(items);
    return newItem;
  }

  /**
   * Remove item from virtual cart
   */
  removeItem(itemId) {
    const items = this.getItems();
    const filtered = items.filter(item => item.id !== itemId);
    this.saveItems(filtered);
    return filtered;
  }

  /**
   * Update item quantity
   */
  updateQuantity(itemId, quantity) {
    const items = this.getItems();
    const item = items.find(i => i.id === itemId);
    if (item) {
      item.quantity = Math.max(1, parseInt(quantity) || 1);
      this.saveItems(items);
      return item;
    }
    return null;
  }

  /**
   * Update variant ID after creation
   */
  updateVariantId(itemId, variantId) {
    const items = this.getItems();
    const item = items.find(i => i.id === itemId);
    if (item) {
      item.variantId = variantId;
      this.saveItems(items);
      return item;
    }
    return null;
  }

  /**
   * Clear all items from virtual cart
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
      return true;
    } catch (error) {
      console.error('Error clearing LocalStorage:', error);
      return false;
    }
  }

  /**
   * Get total count of items
   */
  getItemCount() {
    const items = this.getItems();
    return items.reduce((total, item) => total + (item.quantity || 1), 0);
  }

  /**
   * Get total price
   */
  getTotalPrice() {
    const items = this.getItems();
    return items.reduce((total, item) => {
      const itemPrice = parseFloat(item.price || 0);
      const itemQuantity = parseInt(item.quantity || 1);
      return total + (itemPrice * itemQuantity);
    }, 0);
  }

  /**
   * Get original total price (before wholesale discount)
   */
  getOriginalTotalPrice() {
    const items = this.getItems();
    return items.reduce((total, item) => {
      const itemPrice = parseFloat(item.originalPrice || item.price || 0);
      const itemQuantity = parseInt(item.quantity || 1);
      return total + (itemPrice * itemQuantity);
    }, 0);
  }

  /**
   * Get total wholesale discount amount
   */
  getTotalDiscount() {
    const originalTotal = this.getOriginalTotalPrice();
    const discountedTotal = this.getTotalPrice();
    return originalTotal - discountedTotal;
  }

  /**
   * Convert real Shopify cart items to virtual cart
   */
  async convertRealCartToVirtual() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.items && cart.items.length > 0) {
        const virtualItems = cart.items.map(item => {
          const originalPrice = item.price / 100; // Convert from cents to dollars
          // Extract handle from product URL if available
          let productHandle = null;
          if (item.url) {
            const urlMatch = item.url.match(/\/products\/([^\/]+)/);
            if (urlMatch) {
              productHandle = urlMatch[1];
            }
          }
          return {
            id: this.generateUniqueId(),
            needsCreation: false, // These already exist
            variantId: item.variant_id,
            quantity: item.quantity,
            price: originalPrice, // Will be discounted inside addItem if wholesale
            originalPrice: originalPrice, // Store original price
            formData: null,
            productId: item.product_id,
            regularVariantId: item.variant_id,
            imageUrl: item.image,
            title: item.product_title,
            productHandle: productHandle
          };
        });

        // Add all items to virtual cart
        virtualItems.forEach(item => {
          this.addItem(item);
        });

        // Clear real cart
        await this.clearRealCart();
        
        return virtualItems;
      }
      return [];
    } catch (error) {
      console.error('Error converting real cart to virtual:', error);
      return [];
    }
  }

  /**
   * Clear real Shopify cart
   */
  async clearRealCart() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Remove each item from real cart using jQuery (more reliable)
      if (typeof jQuery !== 'undefined') {
        for (const item of cart.items) {
          await new Promise((resolve) => {
            jQuery.ajax({
              type: 'POST',
              url: '/cart/change.js',
              dataType: 'json',
              data: {
                id: item.variant_id,
                quantity: 0
              },
              success: resolve,
              error: resolve // Continue even if one fails
            });
          });
        }
      } else {
        // Fallback to fetch if jQuery not available
        for (const item of cart.items) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: item.variant_id,
                quantity: 0
              })
            });
          } catch (e) {
            // Continue even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Error clearing real cart:', error);
    }
  }

  /**
   * Create variant via API
   */
  async createVariant(formData) {
    return new Promise((resolve, reject) => {
      jQuery.ajax({
        type: 'POST',
        url: this.ajaxUrl,
        data: {
          action: 'gw_createProduct',
          form_data: formData,
        },
        dataType: 'json',
        success: function (response) {
          let parsedResponse;
          
          try {
            if (typeof response === 'string') {
              if (response.trim().startsWith('<')) {
                if (response.includes('<br />') && response.includes('{"status":')) {
                  const jsonStart = response.lastIndexOf('{');
                  const jsonPart = response.substring(jsonStart);
                  parsedResponse = JSON.parse(jsonPart);
                } else {
                  reject(new Error('Server error (HTML response)'));
                  return;
                }
              } else {
                parsedResponse = JSON.parse(response);
              }
            } else {
              parsedResponse = response;
            }

            if (parsedResponse.status === 'success' && parsedResponse.variant_id) {
              resolve(parsedResponse.variant_id);
            } else {
              reject(new Error(parsedResponse.message || 'Failed to create variant'));
            }
          } catch (e) {
            reject(new Error('Error parsing server response'));
          }
        },
        error: function (xhr, status, error) {
          // Try to extract JSON from error response
          try {
            if (xhr.responseText) {
              if (xhr.responseText.includes('<br />') && xhr.responseText.includes('{"status":')) {
                const jsonStart = xhr.responseText.lastIndexOf('{');
                const jsonPart = xhr.responseText.substring(jsonStart);
                const jsonResponse = JSON.parse(jsonPart);
                if (jsonResponse.status === 'success' && jsonResponse.variant_id) {
                  resolve(jsonResponse.variant_id);
                  return;
                }
              }
            }
          } catch (e) {}

          reject(new Error(error || 'Error communicating with server'));
        }
      });
    });
  }

  /**
   * Add item to real Shopify cart
   */
  async addToRealCart(variantId, quantity, properties) {
    return new Promise((resolve, reject) => {
      const addData = {
        items: [
          {
            id: variantId,
            quantity: quantity,
            properties: properties || {}
          }
        ]
      };

      jQuery.post('/cart/add.js', addData)
        .done((response) => {
          resolve(response);
        })
        .fail((xhr, status, error) => {
          // Try to detect Shopify "already sold out" temporary state
          let responseText = xhr && typeof xhr.responseText === 'string' ? xhr.responseText : '';
          let isTemporarySoldOut = false;
          let shopifyMessage = null;

          if (responseText) {
            try {
              const parsed = JSON.parse(responseText);
              if (parsed && typeof parsed.message === 'string') {
                shopifyMessage = parsed.message;
                if (parsed.message.toLowerCase().includes('already sold out')) {
                  isTemporarySoldOut = true;
                }
              }
            } catch (e) {
              // Non-JSON response, ignore
            }
          }

          if (isTemporarySoldOut) {
            const err = new Error('variant_sold_out_temporarily');
            err.code = 'VARIANT_SOLD_OUT_TEMPORARILY';
            if (shopifyMessage) {
              err.shopifyMessage = shopifyMessage;
            }
            reject(err);
          } else {
            reject(new Error(error || 'Error adding to cart'));
          }
        });
    });
  }

  /**
   * Process checkout - create variants and add to real cart
   */
  async processCheckout(progressCallback) {
    const items = this.getItems();
    const results = {
      success: [],
      failed: []
    };

    let hadTemporarySoldOutError = false;

    if (items.length === 0) {
      return results;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      try {
        let variantId = item.variantId;

        // If item already has a variant ID, use it (skip creation)
        if (variantId) {
          if (progressCallback) {
            progressCallback(i + 1, items.length, `Adding product ${i + 1} to cart...`);
          }
        } else if (item.needsCreation && item.formData) {
          // Create variant via API
          if (progressCallback) {
            progressCallback(i + 1, items.length, `Creating product ${i + 1}...`);
          }
          
          variantId = await this.createVariant(item.formData);
          
          // Update item with variant ID
          this.updateVariantId(item.id, variantId);
        } else if (item.regularVariantId) {
          // Use regular variant ID for non-custom products
          variantId = item.regularVariantId;
          if (progressCallback) {
            progressCallback(i + 1, items.length, `Adding product ${i + 1} to cart...`);
          }
        }

        if (!variantId) {
          throw new Error('No variant ID available');
        }

        // Prepare properties for custom stickers
        let properties = {};
        if (item.formData) {
          const formImages = item.formData.images || '';
          const formShopifyImage = item.formData.shopify_image || '';

          properties = {
            // Mostrar la imagen desde Shopify si está disponible, si no, usar la URL original
            Image: formShopifyImage || formImages,
            Quantity: item.formData.quantity || item.formData.qty || '',
            Size: item.formData.size || '',
            Shape: item.formData.shape || '',
            Underlay: item.formData.underlay || '',
            Notes: item.formData.notes || '',
            // Temporarily disable sending the original external file URL to Shopify line item properties
            // OriginalFile: formImages,
            FileType: this.getFileExtension(formImages)
          };
        }

        // Add to real cart
        if (progressCallback) {
          progressCallback(i + 1, items.length, `Adding product ${i + 1} to cart...`);
        }

        await this.addToRealCart(variantId, item.quantity, properties);
        
        results.success.push(item);
      } catch (error) {
        console.error(`Error processing item ${item.id}:`, error);

        // Mark if this was the temporary "sold out" propagation case
        if (error && (error.code === 'VARIANT_SOLD_OUT_TEMPORARILY' || error.message === 'variant_sold_out_temporarily')) {
          hadTemporarySoldOutError = true;
        }

        results.failed.push({
          item: item,
          error: error.message || 'Unknown error'
        });
      }
    }

    // If we hit the Shopify "already sold out" propagation window, explain it clearly to the user
    if (hadTemporarySoldOutError && results.success.length === 0) {
      alert('Your custom sticker product was just created in Shopify and is still syncing. Please wait a few seconds, then click checkout again to add it to your cart.');
    }

    return results;
  }

  /**
   * Get file extension from URL
   */
  getFileExtension(url) {
    if (!url) return '';
    const match = url.match(/\.([^./\\]+)$/);
    return match ? match[1].toLowerCase() : '';
  }
}

// Initialize global instance
window.VirtualCart = VirtualCart;
window.virtualCart = new VirtualCart();

